<template>
  <div>
    <div class='flex flex-col gap-4 xl:flex-row xl:justify-between xl:items-end'>
      <div v-if='slots.title || slots.description'>
        <h2 class='text-jananavy-900 font-medium'><slot name='title' /></h2>
        <p class='font-light text-sm'><slot name='description' /></p>
      </div>
      <ReportKey v-if='showKey' />
    </div>
    <LoadingLayout :isLoading='isLoading' class='mt-8' style='padding-top: 1px;' :slownessThreshold='0'>
      <ReportNoDataWarning :warn='!view'>
        <Bar :data='view.bar' :options='barOptions' :style='view.height' />
      </ReportNoDataWarning>
    </LoadingLayout>
  </div>
</template>

<script setup>

import { computed, watch, useSlots } from 'vue';

import Chart from 'chart.js/auto';
import { Bar } from 'vue-chartjs';

import LoadingLayout from '@/components/layouts/loading-layout.vue';

import ReportNoDataWarning from '@/components/report/report-no-data-warning.vue';

import ReportKey from '@/components/report/report-key.vue';

import { scoreMap } from '@/kernel/scoring';
import { usePayloadRequest } from '@/kernel/api';

const props = defineProps({
  params: Object,
  dimension: String,
  showKey: Boolean
});

const slots = useSlots();

const request = usePayloadRequest('GET', '/assessment-response/historical-aggregate');

const barOptions = {
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          return `${context.parsed.x.toFixed(2)}%`;
        }
      }
    }
  },
  indexAxis: 'y',
  scales: {
    x: {
      stacked: true,
      max: 100,
      ticks: {
        callback: (value, index, ticks) => {
          return `${value}%`;
        }
      }
    },
    y: {
      stacked: true,
      ticks: {
        autoSkip: false
      }
    }
  },
  maintainAspectRatio: false
}

const isLoading = computed(() => {
  return request.isLoading() || request.getData() === null;
});

const view = computed(() => {

  const data = request.getData();

  if (!data?.length) {
    return null;
  }

  const groups = data.filter(group => group.years[props.params.year.to]);

  if (!groups.length) {
    return null;
  }

  const names = [];

  const totals = {
    '0': [],
    '-1': [],
    '-2': [],
    '-3': [],
    '-100': []
  }

  groups.forEach((group) => {
    const scores = group.years[props.params.year.to].scores;

    const total = Object.values(scores).reduce((accumulator, currentValue) => {
      return accumulator + currentValue;
    }, 0);

    names.push(group.header.name);

    totals['0'].push(scores['0'] / total * 100);
    totals['-1'].push(scores['-1'] / total * 100);
    totals['-2'].push(scores['-2'] / total * 100);
    totals['-3'].push(scores['-3'] / total * 100);
    totals['-100'].push(scores['-100'] / total * 100);
  });

  return {
    height: `height: ${(groups.length * 30) + 30}px`,
    bar: {
      labels: names,
      datasets: Object.keys(totals).map((key) => {
        return {
          label: scoreMap.getLabel(key),
          backgroundColor: scoreMap.getColour(key, 'hex'),
          maxBarThickness: 20,
          data: totals[key]
        }
      })
    }
  }
});

const load = () => {
  if (!props.params) {
    return;
  }

  request.doRequest({
    params: {
      dimension: props.dimension,
      ...props.params
    }
  });
}

watch(() => props.params, () => {
  load();
});

load();

</script>
